import { z } from "zod";
export const OpsSha256Schema = z.string().regex(/^sha256:[a-f0-9]{64}$/u);
export const OpsReleaseServiceArtifactSchema = z.object({
    service: z.string().min(1).max(80).regex(/^[a-z][a-z0-9-]*$/u),
    artifactDigest: OpsSha256Schema
}).strict();
const OpsReleaseIdentityCoreSchema = z.object({
    schema: z.literal("ops.release.v1"),
    services: z.array(OpsReleaseServiceArtifactSchema).min(2).max(32)
}).strict();
export const OpsReleaseIdentitySchema = OpsReleaseIdentityCoreSchema.extend({
    releaseId: OpsSha256Schema
}).strict().superRefine((record, context) => {
    const services = record.services.map(({ service }) => service);
    if (new Set(services).size !== services.length) {
        context.addIssue({ code: "custom", path: ["services"], message: "A release may name each service only once." });
    }
    if (services.some((service, index) => index > 0 && services[index - 1] >= service)) {
        context.addIssue({ code: "custom", path: ["services"], message: "Release services must be in ascending name order." });
    }
    if (record.releaseId !== opsReleaseId(record.services)) {
        context.addIssue({ code: "custom", path: ["releaseId"], message: "The release ID does not match its service artifacts." });
    }
});
/** One immutable record names every artifact that can publish or consume a
 * public Luna turn. A deployment owner may add service roles, but must never
 * calculate separate API/Web identities from different input sets. */
export function createOpsReleaseIdentity(artifacts) {
    const services = artifacts
        .map((artifact) => OpsReleaseServiceArtifactSchema.parse(artifact))
        .sort(compareServiceArtifact);
    return OpsReleaseIdentitySchema.parse({
        schema: "ops.release.v1",
        services,
        releaseId: opsReleaseId(services)
    });
}
export function opsReleaseId(artifacts) {
    const services = artifacts
        .map((artifact) => OpsReleaseServiceArtifactSchema.parse(artifact))
        .sort(compareServiceArtifact);
    return opsSha256({ schema: "ops.release.v1", services });
}
function compareServiceArtifact(left, right) {
    return left.service < right.service ? -1 : left.service > right.service ? 1 : 0;
}
/** Deterministic JSON for identity inputs: object keys are sorted by UTF-16
 * code units, arrays retain order, and non-JSON/invalid-Unicode values fail. */
export function opsCanonicalJson(value) {
    if (value === null)
        return "null";
    if (typeof value === "boolean")
        return value ? "true" : "false";
    if (typeof value === "number") {
        if (!Number.isFinite(value))
            throw new TypeError("Canonical JSON cannot encode a non-finite number.");
        return JSON.stringify(value);
    }
    if (typeof value === "string") {
        assertUnicodeScalarString(value);
        return JSON.stringify(value);
    }
    if (Array.isArray(value))
        return `[${value.map((entry) => opsCanonicalJson(entry)).join(",")}]`;
    if (typeof value !== "object")
        throw new TypeError("Canonical JSON accepts JSON values only.");
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) {
        throw new TypeError("Canonical JSON accepts plain objects only.");
    }
    const record = value;
    const keys = Object.keys(record).sort();
    return `{${keys.map((key) => {
        assertUnicodeScalarString(key);
        if (record[key] === undefined)
            throw new TypeError("Canonical JSON cannot encode undefined.");
        return `${JSON.stringify(key)}:${opsCanonicalJson(record[key])}`;
    }).join(",")}}`;
}
export function opsSha256(value) {
    return `sha256:${sha256Bytes(new TextEncoder().encode(opsCanonicalJson(value)))}`;
}
export function opsSha256Utf8(value) {
    assertUnicodeScalarString(value);
    return `sha256:${sha256Bytes(new TextEncoder().encode(value))}`;
}
function assertUnicodeScalarString(value) {
    for (let index = 0; index < value.length; index += 1) {
        const code = value.charCodeAt(index);
        if (code >= 0xd800 && code <= 0xdbff) {
            const next = value.charCodeAt(index + 1);
            if (!(next >= 0xdc00 && next <= 0xdfff))
                throw new TypeError("Identity text contains an unpaired surrogate.");
            index += 1;
        }
        else if (code >= 0xdc00 && code <= 0xdfff) {
            throw new TypeError("Identity text contains an unpaired surrogate.");
        }
    }
}
const SHA256_CONSTANTS = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
]);
function sha256Bytes(input) {
    const paddedSize = Math.ceil((input.length + 9) / 64) * 64;
    const bytes = new Uint8Array(paddedSize);
    bytes.set(input);
    bytes[input.length] = 0x80;
    const data = new DataView(bytes.buffer);
    const bitLength = input.length * 8;
    data.setUint32(paddedSize - 8, Math.floor(bitLength / 0x1_0000_0000), false);
    data.setUint32(paddedSize - 4, bitLength >>> 0, false);
    const hash = new Uint32Array([
        0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
        0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19
    ]);
    const words = new Uint32Array(64);
    for (let offset = 0; offset < paddedSize; offset += 64) {
        for (let index = 0; index < 16; index += 1)
            words[index] = data.getUint32(offset + index * 4, false);
        for (let index = 16; index < words.length; index += 1) {
            const left = words[index - 15];
            const right = words[index - 2];
            const small0 = rotate(left, 7) ^ rotate(left, 18) ^ (left >>> 3);
            const small1 = rotate(right, 17) ^ rotate(right, 19) ^ (right >>> 10);
            words[index] = (words[index - 16] + small0 + words[index - 7] + small1) >>> 0;
        }
        const state = new Uint32Array(hash);
        for (let index = 0; index < 64; index += 1) {
            const big1 = rotate(state[4], 6) ^ rotate(state[4], 11) ^ rotate(state[4], 25);
            const choose = (state[4] & state[5]) ^ (~state[4] & state[6]);
            const first = (state[7] + big1 + choose + SHA256_CONSTANTS[index] + words[index]) >>> 0;
            const big0 = rotate(state[0], 2) ^ rotate(state[0], 13) ^ rotate(state[0], 22);
            const majority = (state[0] & state[1]) ^ (state[0] & state[2]) ^ (state[1] & state[2]);
            const second = (big0 + majority) >>> 0;
            state[7] = state[6];
            state[6] = state[5];
            state[5] = state[4];
            state[4] = (state[3] + first) >>> 0;
            state[3] = state[2];
            state[2] = state[1];
            state[1] = state[0];
            state[0] = (first + second) >>> 0;
        }
        for (let index = 0; index < hash.length; index += 1)
            hash[index] = (hash[index] + state[index]) >>> 0;
    }
    return [...hash].map((part) => part.toString(16).padStart(8, "0")).join("");
}
function rotate(value, bits) {
    return ((value >>> bits) | (value << (32 - bits))) >>> 0;
}
//# sourceMappingURL=ops-release-identity.js.map